import { formatPhone, isValidPhone, normalizePhone } from '@/lib/phone';
import { isValidEmail } from '@/lib/validators';
import type { PriceAlertChannel, PriceAlertSettings, Subscription } from '@/types/priceAlert';

// Чистые трансформации подписок «лучшая цена продажи» (DTO notification-manager ↔ форма).
// Группа всегда PRICE_MONITORING; каналы — WHATSAPP/EMAIL. Без React — покрыто юнит-тестами.

const PRICE_MONITORING = 'PRICE_MONITORING' as const;

/** Канал считается активным, если подписка включена И в ней есть контакт. */
function isActive(sub: Subscription): boolean {
  if (!sub.enabled) {
    return false;
  }
  if (sub.transportType === 'WHATSAPP') {
    return !!sub.phone && isValidPhone(sub.phone);
  }
  if (sub.transportType === 'EMAIL') {
    return !!sub.email && isValidEmail(sub.email);
  }
  return false;
}

/** Активные каналы фичи из списка подписок — для статус-карточки (порядок стабилен: WhatsApp, Email). */
export function activeChannels(subs: readonly Subscription[]): PriceAlertChannel[] {
  const channels: PriceAlertChannel[] = [];
  if (subs.some((sub) => sub.transportType === 'WHATSAPP' && isActive(sub))) {
    channels.push('WHATSAPP');
  }
  if (subs.some((sub) => sub.transportType === 'EMAIL' && isActive(sub))) {
    channels.push('EMAIL');
  }
  return channels;
}

/** Подписки с бэка → состояние формы. `fallbackEmail` (из профиля) — если своего email ещё нет. */
export function settingsFromSubscriptions(
  subs: readonly Subscription[],
  fallbackEmail = '',
): PriceAlertSettings {
  const whatsapp = subs.find((sub) => sub.transportType === 'WHATSAPP');
  const email = subs.find((sub) => sub.transportType === 'EMAIL');
  return {
    whatsappEnabled: !!whatsapp?.enabled,
    phone: whatsapp?.phone ? formatPhone(whatsapp.phone) : '',
    emailEnabled: !!email?.enabled,
    email: email?.email || fallbackEmail,
  };
}

/**
 * Состояние формы → массив подписок для POST /update. Всегда шлём обе записи (WHATSAPP, EMAIL) с их
 * `enabled`; контакт сохраняем даже у выключенного канала, чтобы повторное включение не требовало
 * перенабора. Телефон нормализуем к `+7XXXXXXXXXX`.
 */
export function subscriptionsFromSettings(settings: PriceAlertSettings): Subscription[] {
  const phone = settings.phone.trim() ? normalizePhone(settings.phone) : null;
  const email = settings.email.trim() || null;
  return [
    { messageGroup: PRICE_MONITORING, transportType: 'WHATSAPP', phone, email: null, enabled: settings.whatsappEnabled },
    { messageGroup: PRICE_MONITORING, transportType: 'EMAIL', phone: null, email, enabled: settings.emailEnabled },
  ];
}

export interface PriceAlertErrors {
  readonly phone?: string;
  readonly email?: string;
}

/**
 * Валидация формы перед сохранением. Контакт требуется только у включённого канала.
 * Выключение обоих каналов допустимо — это отписка. `messages` — i18n-строки (без привязки к t()).
 */
export function validateSettings(
  settings: PriceAlertSettings,
  messages: { readonly required: string; readonly phone: string; readonly email: string },
): PriceAlertErrors {
  const errors: { phone?: string; email?: string } = {};
  if (settings.whatsappEnabled) {
    if (!settings.phone.trim()) {
      errors.phone = messages.required;
    } else if (!isValidPhone(settings.phone)) {
      errors.phone = messages.phone;
    }
  }
  if (settings.emailEnabled) {
    if (!settings.email.trim()) {
      errors.email = messages.required;
    } else if (!isValidEmail(settings.email)) {
      errors.email = messages.email;
    }
  }
  return errors;
}

/** Есть ли в форме изменения относительно исходного состояния (для активации «Сохранить»). */
export function settingsEqual(a: PriceAlertSettings, b: PriceAlertSettings): boolean {
  return (
    a.whatsappEnabled === b.whatsappEnabled &&
    a.emailEnabled === b.emailEnabled &&
    normalizePhone(a.phone) === normalizePhone(b.phone) &&
    a.email.trim() === b.email.trim()
  );
}
